import React, { useState, useRef, useEffect } from 'react';
import { accountPasswordResetRequest, errText } from '../services/api';
import { isNetworkError } from '../services/offlineQueue';
import { T, S } from '../styles/ds';

// How long the emailed link lives. QUOTED from the backend rather than invented: the source of
// truth is `settings.ACCOUNT_PASSWORD_RESET_TTL_MINUTES` (backend/app/config.py), which no config
// endpoint exposes to the frontend today — so this is a deliberate duplicate and the two must be
// changed together. Telling the owner a number the server does not agree with is worse than
// telling them nothing.
// Exported so the reset screen quotes the same number rather than writing a second literal.
export const RESET_LINK_TTL_MINUTES = 30;

// Mirrors the backend's own check exactly — `_EMAIL_RE` and `_validate_email` in
// `app/models/account.py` (trimmed, non-empty, at most 254 characters).
//
// It has to be at LEAST as strict as the server's, and that is the whole point of validating here.
// A malformed address is the ONE input whose response shape differs from the deliberately
// identical success below: the server answers it with a 422 while every other address — registered
// or not — gets the same `{success: true}`. Letting a typo through would therefore re-open exactly
// the "is this address registered?" side channel PH-46 was built to close, because a 422 and a
// success would be two visibly different outcomes on this screen.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const isValidEmail = (v) => {
  const e = (v || '').trim();
  return !!e && e.length <= 254 && EMAIL_RE.test(e);
};

const INVALID_EMAIL_MSG = 'सही ईमेल पता डालें / Enter a valid email address';
// A request that never reached the server is a connection problem, never a delivery claim — the
// same layering AccountSignIn.jsx uses (isNetworkError checked AHEAD of errText).
const NET_ERROR_MSG = 'सर्वर से संपर्क नहीं हो सका — कुछ नहीं भेजा गया। इंटरनेट जाँचें और फिर कोशिश करें / Could not reach the server — nothing was sent. Check the internet and try again';
const GENERIC_ERROR_MSG = 'लिंक नहीं भेजा जा सका — थोड़ी देर बाद कोशिश करें / Could not send the link — please try again in a little while';
// The 429 wait is SHARED with the shop's PIN login. `check_ip_throttle` is keyed on the source IP
// alone, and the staff PIN login (`/auth/login`) reads the same `LoginThrottle` row, so on a shop
// behind one internet connection a run of presses here stops the counter staff signing in at the
// till. That consequence is the part the owner cannot guess, so it is stated rather than implied.
const THROTTLE_SHARED_MSG = 'यही इंतज़ार इसी इंटरनेट कनेक्शन पर दुकान के PIN से साइन इन करने पर भी लागू होता है — इंतज़ार पूरा होने तक काउंटर पर भी साइन इन नहीं हो पाएगा / The same wait also applies to signing in with the shop PIN on this internet connection — until it passes, the counter cannot sign in either';

// Screen 1 of the Owner Account password reset (PH-68): ask for a link.
//
// PH-46 shipped the endpoint behind this screen with one deliberate property — the response is
// identical whether or not the address has an account, so nobody can use this form to find out who
// is registered. Everything below protects that property; do not "improve" the success panel into
// something more helpful.
function AccountForgotPassword({ initialEmail = '', onCancel }) {
  const [email, setEmail] = useState(initialEmail);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [throttleDetail, setThrottleDetail] = useState('');
  const [loading, setLoading] = useState(false);

  // Set true on mount and false ONLY in the cleanup. PH-62's regression was a ref that only ever
  // went false: index.js renders under React.StrictMode, which double-invokes this effect on every
  // mount in development (setup, cleanup, setup again), so a ref the cleanup alone touches is left
  // permanently false and silently no-ops every submit on the component's first real mount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Belt and braces for "a double-click sends exactly one request". The button is disabled while
  // in flight and after success, but `loading` is React state: two clicks dispatched in the SAME
  // tick both read the pre-update value and both would fire. A ref updates synchronously, so the
  // second click is refused before it can reach the network.
  //
  // This matters more here than on an ordinary form. `record_ip_failure` on this endpoint is
  // UNCONDITIONAL — every call burns throttle budget, successes included — and that budget is
  // shared with the till's PIN login (see THROTTLE_SHARED_MSG). One accidental double-click is a
  // wasted press the owner never asked for.
  const inFlightRef = useRef(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inFlightRef.current || sent) return;
    if (!isValidEmail(email)) {
      setError(INVALID_EMAIL_MSG);
      setThrottleDetail('');
      return;   // nothing is sent: a typo must never reach the server (see EMAIL_RE above)
    }
    inFlightRef.current = true;
    setLoading(true);
    setError('');
    setThrottleDetail('');
    try {
      await accountPasswordResetRequest(email.trim());
      if (!mountedRef.current) return;
      setSent(true);
    } catch (err) {
      if (!mountedRef.current) return;
      // isNetworkError FIRST, errText second — the same order AccountSignIn.jsx uses. A request
      // that never arrived must read as a connection problem; saying anything about a link having
      // been sent would be a claim nothing supports.
      if (isNetworkError(err)) {
        setError(NET_ERROR_MSG);
      } else if (err?.response?.status === 429) {
        // The wait comes from the server's own message, never a client-side countdown — this
        // browser has no idea when the block started or how far the back-off has escalated
        // (30s → 60s → 120s → 300s → 900s → 1800s), so any number computed here would be a guess
        // presented as a fact.
        setError(errText(err, GENERIC_ERROR_MSG));
        setThrottleDetail(THROTTLE_SHARED_MSG);
      } else {
        setError(errText(err, GENERIC_ERROR_MSG));
      }
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  };

  // Same page-wrapper shape as AccountSignIn.jsx — design-system tokens only; the two pixel values
  // here are plain layout, not colours, exactly as that screen and Login.jsx do it.
  const wrapStyle = { minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: T.font };
  const btnDisabled = { opacity: 0.5, cursor: 'default' };
  const submitDisabled = loading || sent || !email.trim();

  return (
    <div style={wrapStyle}>
      <div style={{ marginBottom: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: T.ink, letterSpacing: -0.5 }}>पासवर्ड रीसेट करें</div>
        <div style={{ fontSize: 13, color: T.sec, marginTop: 4 }}>Reset your account password</div>
      </div>

      <div style={{ ...S.cardPad, width: '100%', maxWidth: 360 }}>
        {sent ? (
          // The success panel. Identical copy, icon and layout for a registered and an unregistered
          // address — it says "if this email is registered" rather than claiming delivery, because
          // this screen genuinely does not know which it was and must not appear to.
          //
          // Neutral/positive tokens, never S.alertDetail: asking for a link worked, and painting a
          // good outcome in the app's error red would read as a failure.
          <div>
            <div
              role="status"
              style={{
                padding: '14px 16px', background: T.green.bg, border: `1px solid ${T.green.border}`,
                borderRadius: T.rs, fontFamily: T.font, textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 22, marginBottom: 6 }} aria-hidden="true">✉️</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.green.text, marginBottom: 6 }}>
                अगर यह ईमेल हमारे पास दर्ज है, तो रीसेट लिंक भेज दिया गया है / If this email is registered with us, a reset link has been sent
              </div>
              <div style={{ fontSize: 12, color: T.sec }}>
                यह लिंक {RESET_LINK_TTL_MINUTES} मिनट तक काम करेगा — उसके बाद नया लिंक माँगना होगा / This link works for {RESET_LINK_TTL_MINUTES} minutes — after that you will need to ask for a new one
              </div>
            </div>
            <div style={{ fontSize: 12, color: T.mute, marginTop: 12, textAlign: 'center' }}>
              ईमेल न दिखे तो स्पैम/जंक फ़ोल्डर देखें / If you cannot see the email, check your spam or junk folder
            </div>
          </div>
        ) : (
          // Codex review finding on PR #68 (real, confirmed): noValidate. Without it, a browser's
          // native type="email" constraint validation blocks the submit event for a malformed
          // address before handleSubmit ever runs, so the bilingual INVALID_EMAIL_MSG below never
          // shows — only a transient, browser-locale-dependent tooltip. handleSubmit's own
          // isValidEmail() check is the one source of truth for this field.
          <form onSubmit={handleSubmit} noValidate>
            <div style={{ fontSize: 12, color: T.sec, marginBottom: 12 }}>
              अपने खाते का ईमेल डालें — हम उस पर पासवर्ड बदलने का लिंक भेजेंगे / Enter your account email and we will send a link to set a new password
            </div>
            <label htmlFor="reset-request-email" style={S.label}>ईमेल / Email</label>
            <input
              id="reset-request-email" type="email" autoComplete="email" autoFocus required
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); setThrottleDetail(''); }}
              placeholder="you@example.com" style={{ ...S.input, marginBottom: 12 }}
            />
            {error && (
              <div role="alert" style={{ ...S.alertDetail, textAlign: 'center' }}>
                {error}
                {throttleDetail && (
                  <div style={{ marginTop: 6, fontWeight: 600 }}>{throttleDetail}</div>
                )}
              </div>
            )}
            <button
              type="submit" disabled={submitDisabled}
              style={{ ...S.btnPri, width: '100%', ...(submitDisabled ? btnDisabled : {}) }}
            >
              {loading ? 'भेजा जा रहा है...' : 'रीसेट लिंक भेजें / Send reset link →'}
            </button>
          </form>
        )}

        {/* Deliberately no resend control in this pass, and nothing that invites a repeat press:
            no retry-on-mount and no automatic retry either. Every call burns throttle budget shared
            with the till's PIN login, and the link already lives 30 minutes — so a resend button
            buys little and is the easiest way for an owner to lock their own counter out. */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <button
            type="button" onClick={onCancel}
            style={{ ...S.btnSec, background: 'none', border: 'none', color: T.mute, fontSize: 12, cursor: 'pointer' }}
          >
            ← साइन इन पर वापस जाएँ / Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
}

export default AccountForgotPassword;
